import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import PriceCatalog from '@/components/ui/PriceCatalog/PriceCatalog';
import { priceCatalogRequestTypePropType } from '@/constants/propTypes';
import dametisApi from '@/helpers/dametisApi';
import strapiApi from '@/helpers/strapi/api';

import productPlaceholder from './lib/product-placeholder.png';

const prepareWithStrapiSlug = async products => {
  const productPromises = products.map(async product => {
    const data = (await strapiApi.getProductTypeByDametisId(product.id))?.data;
    const slug = data?.data?.[0].attributes.slug;

    return { ...product, slug };
  });

  const result = await Promise.all(productPromises);

  return result;
};

const prepareAttributes = (attributes, attributeNames) =>
  attributes
    .filter(({ key }) => attributeNames.includes(key))
    .map(({ key, value, unit }) => ({ name: key, value, unit }));

const prepareDametisProducts = (fetchedData = {}, products = []) => {
  const preparedProducts = products
    .map(product => {
      const productData = fetchedData[product.salesforceId];

      if (productData) {
        return {
          id: productData.id,
          headline: productData.name,
          image:
            productData.images?.length > 0
              ? productData.images[0].url
              : productPlaceholder.src,
          price: productData.rental_price,
          initialPrice: productData.premium_product_original_price,
          priceBasis: productData.rental_price_basis,
          attributes: prepareAttributes(
            productData.attributes,
            product.attributes
          ),
          salesforceId: productData.salesforce_id,
          productReferenceId: productData.id?.toString(),
          automatedOffer: Boolean(productData.automated_offer),
          sendAutomatedOffer: Boolean(productData.send_automated_offer),
          minLeadTime: productData.min_lead_time,
          rentalPriceBasis: productData.rental_price_basis,
          rentalPrice: productData.rental_price,
          productGroupId: productData.product_group_id,
          productGroupName: productData.product_group_name,
        };
      } else {
        return null;
      }
    })
    .filter(product => !!product);

  return preparedProducts;
};

const PriceCatalogContainer = ({ priceCatalog }) => {
  const products = useMemo(
    () =>
      priceCatalog.tile?.map(product => ({
        headline: product.name,
        image: product.image.data,
        price: product.price,
        initialPrice: product.initialPrice,
        priceBasis: product.priceBasis,
        attributes: product.attributes,
        url: product.url,
      })) || [],
    [priceCatalog.tile]
  );

  const dametisProductsBase = useMemo(
    () =>
      priceCatalog.dametisTile?.map(product => ({
        ...product,
        attributes: product?.attributes?.data?.map(
          attribute => attribute?.attributes?.blob?.name
        ),
      })) || [],
    [priceCatalog.dametisTile]
  );

  const [dametisProducts, setDametisProducts] = useState([]);
  const [loading, setLoading] = useState(dametisProductsBase.length > 0);

  const fetchDametisProducts = useCallback(async () => {
    const dametisProductsSalesforceIds = dametisProductsBase.map(
      product => product.salesforceId
    );

    try {
      if (dametisProductsSalesforceIds.length > 0) {
        setLoading(true);
        const response = await dametisApi.getProductsBySalesforceId(
          dametisProductsSalesforceIds
        );
        if (response.status >= 200 && response.status < 300) {
          const preparedDametisProducts = prepareDametisProducts(
            response.data,
            dametisProductsBase
          );

          const productsWithSlug = await prepareWithStrapiSlug(
            preparedDametisProducts
          );

          setDametisProducts(productsWithSlug);
        }
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  }, [dametisProductsBase]);

  useEffect(() => {
    fetchDametisProducts();
  }, [fetchDametisProducts]);

  return (
    <PriceCatalog
      products={[...products, ...dametisProducts]}
      requestType={priceCatalog.requestType}
      loading={loading}
    />
  );
};

PriceCatalogContainer.propTypes = {
  priceCatalog: PropTypes.shape({
    tile: PropTypes.array.isRequired,
    dametisTile: PropTypes.array.isRequired,
    requestType: priceCatalogRequestTypePropType,
  }),
};

export default PriceCatalogContainer;
